/**
 * "LIVE · 12 s ago": a chart drawn from a live source says so, and how old its
 * newest reading is.
 *
 * Shown on Explore's chart header and on dashboard tiles whose source has no
 * history (`docs/plans/data-sources.md`, "Live charts"). The age counts up by
 * itself, once a second, so a chart that has stopped receiving data shows it
 * without anybody refreshing anything. Past `stale-after` seconds the dot goes
 * hollow and amber and the tooltip says nothing newer has arrived - the colour
 * is never the only carrier: the age is in the words.
 *
 * `newest` is epoch seconds (UTC), the chart's newest x - `last_x` on a plot
 * response. Null draws the chip without an age.
 *
 * `mode` says whether the chart keeps up: `ticking` (the dot breathes; a click
 * pauses), `paused` (the word says so; a click resumes) or `static` (a fixed
 * window, nothing to pause - not a button). A click fires `live-toggle`.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../lit-base.ts';
import { shortAge } from '../features/plot/render/last-value-tags.ts';

@customElement('live-chip')
export class LiveChip extends LightElement {
  /** Newest reading, epoch seconds UTC; null when there is none. */
  @property({ type: Number }) newest: number | null = null;
  /** Seconds after which the newest reading counts as stale. */
  @property({ type: Number, attribute: 'stale-after' }) staleAfter = 180;
  @property() mode: 'ticking' | 'paused' | 'static' = 'static';
  /** Seconds between updates while ticking, for the tooltip. */
  @property({ type: Number }) cadence: number | null = null;

  @state() private now = Date.now() / 1000;
  private timer: number | undefined;

  override connectedCallback(): void {
    super.connectedCallback();
    this.timer = window.setInterval(() => (this.now = Date.now() / 1000), 1000);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.clearInterval(this.timer);
  }

  override render(): TemplateResult {
    const age = this.newest != null && Number.isFinite(this.newest) ? this.now - this.newest : null;
    const stale = age != null && age > this.staleAfter;
    const reading =
      age == null
        ? 'Live data.'
        : stale
          ? `Live data. The newest reading is ${shortAge(age)} old - nothing newer has arrived.`
          : `Live data. The newest reading is ${shortAge(age)} old.`;
    const every = this.cadence ? ` every ${shortAge(this.cadence)}` : '';
    const action =
      this.mode === 'ticking'
        ? ` Updating${every}. Click to pause.`
        : this.mode === 'paused'
          ? ' Paused. Click to resume.'
          : '';
    const body = html`<span class="live-chip-dot" aria-hidden="true"></span
      ><span class="live-chip-word">${this.mode === 'paused' ? 'Paused' : 'Live'}</span
      >${age == null ? nothing : html`<span class="live-chip-age">${shortAge(age)} ago</span>`}`;
    const cls = `live-chip is-${this.mode} ${stale ? 'is-stale' : ''}`;
    if (this.mode === 'static') return html`<span class=${cls} title=${reading}>${body}</span>`;
    return html`<button
      type="button"
      class=${cls}
      title=${reading + action}
      aria-pressed=${this.mode === 'paused' ? 'true' : 'false'}
      @click=${() => this.dispatchEvent(new CustomEvent('live-toggle', { bubbles: true, composed: true }))}
    >
      ${body}
    </button>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'live-chip': LiveChip;
  }
}
